
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navigation = [
    { name: t('home'), href: '/' },
    { name: t('about'), href: '/about' },
  ];

  const projectLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Wild Cherry', href: '/project/wildcherry' },
    { name: "Le Veau d'Or", href: '/project/leveaudor' },
    { name: 'Le Rock', href: '/project/lerock' },
    { name: 'Frenchette', href: '/project/frenchette' },
  ];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const handleMobileNavClick = (href: string) => {
    setIsMenuOpen(false);
    // Scroll to top on mobile when navigating
    if (window.innerWidth < 768) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/b035185ce40665b0d42a9231037815e9/1b3ff7e0a6319a84e75a0e9a83b1d24d.jpeg" 
              alt="Logo" 
              className="h-12 w-auto object-contain md:h-24"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-16">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-2xl font-medium transition-colors duration-200 hover:text-teal-600 cursor-pointer ${
                  location.pathname === item.href
                    ? 'text-teal-600 border-b-2 border-teal-600 pb-1'
                    : 'text-gray-700'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Language Switcher */}
            <div className="flex items-center space-x-8">
              <button
                onClick={() => changeLanguage('en')}
                className={`text-2xl font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  i18n.language === 'en' ? 'text-teal-600 font-semibold' : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                English
              </button>
              <span className="text-gray-300 text-2xl">|</span>
              <button
                onClick={() => changeLanguage('fr')}
                className={`text-2xl font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  i18n.language === 'fr' ? 'text-teal-600 font-semibold' : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                Français
              </button>
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center justify-center flex-1">
            {/* Mobile Language Switcher centered */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => changeLanguage('en')}
                className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  i18n.language === 'en' ? 'text-teal-600 font-semibold' : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                English
              </button>
              <span className="text-gray-300 text-sm">|</span>
              <button
                onClick={() => changeLanguage('fr')}
                className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  i18n.language === 'fr' ? 'text-teal-600 font-semibold' : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                Français
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hamburger Menu - Mobile Only */}
      <div className="fixed top-4 right-4 z-50 md:hidden">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="bg-black border border-white/20 shadow-lg rounded-lg p-2 hover:bg-gray-900 transition-colors duration-200 cursor-pointer"
        >
          <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 flex items-center justify-center text-white`} style={{ strokeWidth: '3px', fontWeight: 'bold' }}></i>
        </button>

        {/* Mobile Dropdown Menu */}
        {isMenuOpen && (
          <div className="absolute top-full right-0 mt-1 bg-black border border-white/20 shadow-lg rounded-lg py-0.5 min-w-[100px]">
            {projectLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => handleMobileNavClick(link.href)}
                className="block px-3 py-1 text-xs font-medium text-white hover:bg-gray-900 hover:text-teal-400 transition-colors duration-200 cursor-pointer whitespace-nowrap text-right"
              >
                {link.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
